import React, { useMemo, useState } from "react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { fmtXOF, fmtDate } from "../lib/format";
import api from "../api/client";
import { toast } from "sonner";
import { Calculator, Download, Sparkles, TrendingDown, TrendingUp, Loader2 } from "lucide-react";

export default function CreditSimulator() {
  const [form, setForm] = useState({
    prix_total: 50000000,
    apport_initial: 10000000,
    duree_annees: 15,
    taux_annuel: 7.5,
    mode: "constant",
    nom: "",
    telephone: "",
    email: "",
    bien_ref: "",
  });
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const dureeMois = useMemo(() => Math.max(1, Math.round(Number(form.duree_annees) * 12)), [form.duree_annees]);

  const simulate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/credit/simulate", {
        prix_total: Number(form.prix_total),
        apport_initial: Number(form.apport_initial),
        duree_mois: dureeMois,
        taux_annuel: Number(form.taux_annuel),
        mode: form.mode,
      });
      setSim(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.post(
        "/credit/simulate/pdf",
        {
          simulation: {
            prix_total: Number(form.prix_total),
            apport_initial: Number(form.apport_initial),
            duree_mois: dureeMois,
            taux_annuel: Number(form.taux_annuel),
            mode: form.mode,
          },
          client: {
            nom: form.nom,
            telephone: form.telephone,
            email: form.email,
            bien_ref: form.bien_ref,
          },
        },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `simulation_credit_${(form.nom || "TPCO").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Simulation PDF téléchargée");
    } catch (e) {
      toast.error("Erreur PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-[1500px] mx-auto">
        <PageHeader
          kicker="Outil commercial"
          title="Simulateur de crédit"
          subtitle="Présentez en quelques secondes un plan de financement personnalisé à votre prospect, puis exportez-le en PDF."
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* === COLONNE GAUCHE : Inputs === */}
          <div className="lg:col-span-2 space-y-5">
            <div className="tpco-card p-6" data-testid="sim-inputs">
              <h3 className="font-display text-base font-bold text-[#0F1A3D] mb-4 flex items-center gap-2">
                <Calculator className="w-4 h-4 text-[#1E3A8A]" /> Paramètres du financement
              </h3>

              <div className="space-y-4">
                <Field label="Prix du bien (FCFA)">
                  <Input type="number" value={form.prix_total} onChange={(e) => setForm({ ...form, prix_total: e.target.value })} data-testid="sim-prix" />
                </Field>
                <Field label="Apport personnel (FCFA)">
                  <Input type="number" value={form.apport_initial} onChange={(e) => setForm({ ...form, apport_initial: e.target.value })} data-testid="sim-apport" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Durée (années)">
                    <Input type="number" min="1" max="50" step="0.5" value={form.duree_annees} onChange={(e) => setForm({ ...form, duree_annees: e.target.value })} data-testid="sim-duree" />
                  </Field>
                  <Field label="Taux annuel (%)">
                    <Input type="number" step="0.1" value={form.taux_annuel} onChange={(e) => setForm({ ...form, taux_annuel: e.target.value })} data-testid="sim-taux" />
                  </Field>
                </div>

                <Field label="Mode d'amortissement">
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { v: "constant", l: "Mensualités constantes", ic: TrendingUp, d: "Le plus courant" },
                      { v: "degressif", l: "Amortissement constant", ic: TrendingDown, d: "Mensualités dégressives" },
                    ].map((m) => {
                      const Ic = m.ic;
                      const sel = form.mode === m.v;
                      return (
                        <button
                          key={m.v}
                          type="button"
                          onClick={() => setForm({ ...form, mode: m.v })}
                          className={`p-3 rounded-lg border-2 text-left transition ${sel ? "border-[#1E3A8A] bg-[#E8EBFA]" : "border-[#E2E5F1] hover:border-[#1E3A8A]/40"}`}
                          data-testid={`sim-mode-${m.v}`}
                        >
                          <Ic className={`w-4 h-4 mb-1 ${sel ? "text-[#1E3A8A]" : "text-[#4B5775]"}`} />
                          <div className="text-xs font-bold text-[#0F1A3D] leading-tight">{m.l}</div>
                          <div className="text-[10px] text-[#4B5775] mt-0.5">{m.d}</div>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                <button
                  type="button"
                  onClick={simulate}
                  disabled={loading || !form.prix_total || !form.duree_annees}
                  className="w-full h-11 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="sim-calc-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
                  Calculer la mensualité
                </button>
              </div>
            </div>

            {/* Coords prospect pour PDF */}
            <div className="tpco-card p-6">
              <h3 className="font-display text-base font-bold text-[#0F1A3D] mb-3">Personnaliser le PDF (optionnel)</h3>
              <div className="space-y-3">
                <Field label="Nom du prospect">
                  <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="M. Mamadou DIOP" data-testid="sim-nom" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Téléphone"><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="+221..." /></Field>
                  <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="prospect@..." /></Field>
                </div>
                <Field label="Bien concerné">
                  <Input value={form.bien_ref} onChange={(e) => setForm({ ...form, bien_ref: e.target.value })} placeholder="TPCO Almadies Tower · Appt 302" />
                </Field>
                <button
                  type="button"
                  onClick={downloadPdf}
                  disabled={!sim || downloading}
                  className="w-full h-11 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="sim-pdf-btn"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Exporter en PDF
                </button>
              </div>
            </div>
          </div>

          {/* === COLONNE DROITE : Résultats === */}
          <div className="lg:col-span-3">
            {!sim ? (
              <div className="tpco-card p-12 text-center">
                <Calculator className="w-12 h-12 text-[#1E3A8A] mx-auto mb-3 opacity-50" />
                <div className="font-display text-lg font-bold text-[#0F1A3D] mb-1">Aucune simulation pour le moment</div>
                <div className="text-sm text-[#4B5775]">Renseignez les paramètres puis cliquez sur <strong>Calculer</strong>.</div>
              </div>
            ) : (
              <div className="space-y-5" data-testid="sim-result">
                {/* Highlight */}
                <div className="bg-gradient-to-br from-[#0F1A3D] to-[#1E3A8A] text-white rounded-2xl p-7">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold mb-3">
                    <Sparkles className="w-3.5 h-3.5 text-[#E91E63]" /> Mensualité estimée
                  </div>
                  {sim.mode === "constant" ? (
                    <div className="font-display text-4xl lg:text-5xl font-extrabold leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {fmtXOF(sim.mensualite)}
                    </div>
                  ) : (
                    <div>
                      <div className="font-display text-2xl lg:text-3xl font-extrabold leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {fmtXOF(sim.mensualite_premiere)} <span className="text-white/40 mx-2">→</span> {fmtXOF(sim.mensualite_derniere)}
                      </div>
                      <div className="text-xs text-white/60 mt-2">de la 1ère à la dernière échéance (dégressif)</div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-4 mt-6 pt-5 border-t border-white/10">
                    <Stat label="Capital emprunté" value={fmtXOF(sim.capital_emprunte)} />
                    <Stat label="Total à payer" value={fmtXOF(sim.total_a_payer)} />
                    <Stat label="Coût du crédit" value={fmtXOF(sim.cout_credit)} accent />
                  </div>
                </div>

                {/* Tableau d'amortissement */}
                <div className="tpco-card overflow-hidden">
                  <div className="px-5 py-3 border-b border-[#E2E5F1] flex items-center justify-between">
                    <h3 className="font-display text-sm font-bold text-[#0F1A3D]">Tableau d'amortissement · {sim.duree_mois} mois</h3>
                    <div className="text-xs text-[#4B5775]">Début : {fmtDate(sim.date_debut)} · Fin : {fmtDate(sim.date_fin)}</div>
                  </div>
                  <div className="max-h-[480px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F1F3FB] sticky top-0">
                        <tr className="text-[10px] uppercase tracking-wider text-[#4B5775]">
                          <th className="text-left px-4 py-2 font-bold">N°</th>
                          <th className="text-left px-3 py-2 font-bold">Échéance</th>
                          <th className="text-right px-3 py-2 font-bold">Mensualité</th>
                          <th className="text-right px-3 py-2 font-bold">Intérêt</th>
                          <th className="text-right px-3 py-2 font-bold">Capital</th>
                          <th className="text-right px-4 py-2 font-bold">Reste dû</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F1F3FB] font-mono">
                        {sim.echeancier.map((r) => (
                          <tr key={r.numero} className="hover:bg-[#F8FAFC]">
                            <td className="px-4 py-1.5 font-bold text-[#0F1A3D]">#{r.numero}</td>
                            <td className="px-3 py-1.5 text-[#4B5775]">{fmtDate(r.date_echeance)}</td>
                            <td className="px-3 py-1.5 text-right font-bold">{fmtXOF(r.mensualite)}</td>
                            <td className="px-3 py-1.5 text-right text-[#E91E63]">{fmtXOF(r.interet)}</td>
                            <td className="px-3 py-1.5 text-right">{fmtXOF(r.amortissement)}</td>
                            <td className="px-4 py-1.5 text-right text-[#4B5775]">{fmtXOF(r.capital_restant)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-white/50 font-bold">{label}</div>
      <div className={`font-bold text-sm mt-1 ${accent ? "text-[#E91E63]" : ""}`}>{value}</div>
    </div>
  );
}
